import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const WEEK_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const productivityUnit = (workType) => {
  if(workType==='DRILLING') return 'ft/hr';
  if(workType==='WIRE_SAW') return 'Sq.m/hr';
  if(workType==='BLASTING') return 'holes/hr';
  if(workType==='TRANSPORT') return 'trips/hr';
  return 'units/hr';
};

const calcRate = (entry) => {
  const d = entry.data || {};
  const wt = entry.work_type;
  const totalMins = (parseFloat(d.running_hours||d.engine_hours_start||0) * 60) + parseFloat(d.running_minutes||0);
  const totalHrs = totalMins / 60;

  if(wt==='DRILLING'){
    const machines = d.selected_machines||[];
    const results = [];
    machines.forEach(m=>{
      const md = (d.machine_data||{})[m]||{};
      const hrs = ((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
      const totalFt = (md.holes||[]).reduce((s,h)=>s+(parseFloat(h.running_feet)||0),0);
      if(hrs>0 && totalFt>0) results.push({ machine:m, hrs, output:totalFt, unit:'ft', rate:(totalFt/hrs).toFixed(2) });
    });
    return results;
  }
  if(wt==='WIRE_SAW'){
    const machines = d.selected_machines||[];
    const results = [];
    machines.forEach(m=>{
      const md = (d.machine_data||{})[m]||{};
      const hrs = ((parseFloat(md.running_hours||0)*60)+parseFloat(md.running_minutes||0))/60;
      const area = parseFloat(md.cutting_area||0);
      if(hrs>0 && area>0) results.push({ machine:`${m} Wire Saw`, hrs, output:area, unit:'Sq.m', rate:(area/hrs).toFixed(2) });
    });
    return results;
  }
  if(wt==='BLASTING'){
    const holes = parseFloat(d.hole_feet||0);
    if(totalHrs>0 && holes>0) return [{ machine:'Blasting', hrs:totalHrs, output:holes, unit:'ft', rate:(holes/totalHrs).toFixed(2) }];
  }
  if(wt==='TRANSPORT'){
    const trips = parseFloat(d.trips||0);
    if(totalHrs>0 && trips>0) return [{ machine:`${d.vehicle_type||'Vehicle'}`, hrs:totalHrs, output:trips, unit:'trips', rate:(trips/totalHrs).toFixed(2) }];
  }
  return null;
};

const TrendArrow = ({ current, previous }) => {
  if(!previous || previous===0) return null;
  const diff = ((current - previous) / previous * 100).toFixed(1);
  const up = current >= previous;
  return (
    <span style={{ fontSize:11, fontWeight:500, color:up?'var(--green)':'var(--red)', marginLeft:6 }}>
      {up?'▲':'▼'} {Math.abs(diff)}%
    </span>
  );
};

const MiniBar = ({ values, color }) => {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display:'flex', gap:3, alignItems:'flex-end', height:40 }}>
      {WEEK_DAYS.map((day, i) => {
        const h = values[i] ? (values[i]/max)*36 : 0;
        const isToday = i === new Date().getDay()-1;
        return (
          <div key={day} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <div style={{ width:18, height:h||2, background:isToday?color:'var(--border)', borderRadius:3, transition:'height 0.3s' }}/>
            <div style={{ fontSize:9, color:'var(--text-muted)' }}>{day[0]}</div>
          </div>
        );
      })}
    </div>
  );
};

export default function DashboardPage() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sessions, setSessions]   = useState([]);
  const [productivity, setProductivity] = useState([]);
  const [weekData, setWeekData]   = useState({});
  const [lowStockItems, setLowStockItems] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/reports/dashboard/'),
      api.get('/operations/sessions/?page_size=200&ordering=-started_date'),
      api.get('/purchase/items/?low_stock=true&page_size=50'),
    ]).then(([r, sr, lsr]) => {
      setData(r.data.dashboard);
      const allSessions = sr.data.results||[];
      setSessions(allSessions);
      setLowStockItems(lsr.data.results||[]);

      // Calculate today's productivity
      const today = new Date().toISOString().split('T')[0];
      const todaySessions = allSessions.filter(s => s.started_date === today);
      const prodItems = [];

      todaySessions.forEach(session => {
        try {
          const parsed = JSON.parse(session.notes||'{}');
          const entries = parsed.day_entries||[];
          entries.forEach(entry => {
            const rates = calcRate(entry);
            console.log('entry:', entry.work_type, 'rates:', rates);
            if(rates?.length) {
              rates.forEach(r => prodItems.push({ ...r, session_number: session.session_number, work_type: entry.work_type }));
            }
          });
        } catch {}
      });
      setProductivity(prodItems);

      // Calculate week data (Mon-Sun) per work type
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0=Sun
      const monday = new Date(now);
      monday.setDate(now.getDate() - (dayOfWeek===0?6:dayOfWeek-1));

      const weekMap = { DRILLING:Array(7).fill(0), WIRE_SAW:Array(7).fill(0), BLASTING:Array(7).fill(0), TRANSPORT:Array(7).fill(0) };

      allSessions.forEach(session => {
        const sDate = new Date(session.started_date);
        const dayIdx = Math.floor((sDate - monday) / (1000*60*60*24));
        if(dayIdx < 0 || dayIdx > 6) return;
        try {
          const parsed = JSON.parse(session.notes||'{}');
          const entries = parsed.day_entries||[];
          entries.forEach(entry => {
            const rates = calcRate(entry);
            if(rates?.length && weekMap[entry.work_type]) {
              const avgRate = rates.reduce((s,r)=>s+parseFloat(r.rate),0)/rates.length;
              weekMap[entry.work_type][dayIdx] = Math.max(weekMap[entry.work_type][dayIdx], avgRate);
            }
          });
        } catch {}
      });
      setWeekData(weekMap);
    }).finally(() => setLoading(false));
  }, []);

  if(loading) return (
    <Layout title="Dashboard" subtitle="Overview of your operations">
      <div className="loading"><div className="spinner"/> Loading dashboard...</div>
    </Layout>
  );

  const d     = data || {};
  const emp   = d.employees  || {};
  const ops   = d.operations || {};
  const prod  = d.production || {};
  const sales = d.sales      || {};

  return (
    <Layout
      title="Dashboard"
      subtitle={new Date().toLocaleDateString('en-IN', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
    >
      {/* KPI Row */}
      <div className="kpi-grid">
        <div className="kpi-card blue">
          <div className="kpi-value">{emp.total || 0}</div>
          <div className="kpi-label">Total Employees</div>
          <div className="kpi-sub">{emp.present_today || 0} present today</div>
        </div>
        <div className="kpi-card brand">
          <div className="kpi-value">{ops.active_sessions || 0}</div>
          <div className="kpi-label">Active Sessions</div>
          <div className="kpi-sub">{ops.total_machines || 0} machines</div>
        </div>
        <div className="kpi-card green">
          <div className="kpi-value">{prod.slabs_in_stock || 0}</div>
          <div className="kpi-label">Slabs in Stock</div>
          <div className="kpi-sub">{prod.slabs_sold || 0} sold</div>
        </div>
        <div className="kpi-card amber">
          <div className="kpi-value">₹{Number(sales.total_revenue || 0).toLocaleString('en-IN')}</div>
          <div className="kpi-label">Total Revenue</div>
          <div className="kpi-sub">₹{Number(sales.pending_payments || 0).toLocaleString('en-IN')} pending</div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div style={{ background:'#FDE8E8', border:'1px solid #fecaca', borderRadius:'var(--radius)', padding:'14px 18px', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <span style={{ fontSize:18 }}>⚠️</span>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--red)' }}>
              {lowStockItems.length} Item{lowStockItems.length>1?'s are':' is'} Low on Stock!
            </div>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {lowStockItems.map((item,i)=>(
              <div key={i} style={{ background:'#fff', border:'1px solid #fecaca', borderRadius:'var(--radius-sm)', padding:'6px 12px', fontSize:12 }}>
                <span style={{ fontWeight:600, color:'var(--red)' }}>{item.name}</span>
                <span style={{ color:'var(--text-muted)', marginLeft:6 }}>
                  {item.category_name} · Stock: <span style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--red)' }}>{parseFloat(item.current_stock).toFixed(2)} {item.unit}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Productivity Section */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Today's Productivity — Per Machine</div>
            <div className="card-subtitle">{new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
          </div>
        </div>

        {productivity.length === 0 ? (
          <div style={{ padding:'20px 0', textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
            No work sessions recorded today. Start a work session to see productivity metrics.
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Work Type</th>
                  <th>Hours</th>
                  <th>Output</th>
                  <th>Rate</th>
                  <th>This Week</th>
                </tr>
              </thead>
              <tbody>
                {productivity.map((item, i) => {
                  const weekVals = weekData[item.work_type]||Array(7).fill(0);
                  const colors = { DRILLING:'var(--brand)', WIRE_SAW:'var(--blue)', BLASTING:'var(--red)', TRANSPORT:'var(--amber)' };
                  const color = colors[item.work_type]||'var(--brand)';
                  const hrs = Math.floor(item.hrs);
                  const mins = Math.round((item.hrs - hrs) * 60);
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{item.machine}</td>
                      <td><span className="badge badge-blue">{item.work_type.replace('_',' ')}</span></td>
                      <td style={{ fontFamily:'DM Mono, monospace', fontSize:13 }}>{hrs}h {mins}m</td>
                      <td style={{ fontFamily:'DM Mono, monospace', fontSize:13, color:'var(--brand)', fontWeight:600 }}>
                        {item.output} {item.unit}
                      </td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontFamily:'DM Mono, monospace', fontSize:15, fontWeight:500, color }}>
                            {item.rate}
                          </span>
                          <span style={{ fontSize:11, color:'var(--text-muted)' }}>{item.unit}/hr</span>
                        </div>
                      </td>
                      <td><MiniBar values={weekVals} color={color}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly Productivity Chart */}
      {Object.keys(weekData).some(k=>weekData[k].some(v=>v>0)) && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header">
            <div>
              <div className="card-title">Weekly Productivity — Mon to Sun</div>
              <div className="card-subtitle">Average rate per hour by work type</div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
            {[
              { key:'DRILLING', label:'Drilling', unit:'ft/hr', color:'var(--brand)' },
              { key:'WIRE_SAW', label:'Wire Saw', unit:'Sq.m/hr', color:'var(--blue)' },
              { key:'BLASTING', label:'Blasting', unit:'ft/hr', color:'var(--red)' },
              { key:'TRANSPORT', label:'Transport', unit:'trips/hr', color:'var(--amber)' },
            ].filter(wt => weekData[wt.key]?.some(v=>v>0)).map(wt => {
              const vals = weekData[wt.key]||Array(7).fill(0);
              const max = Math.max(...vals, 1);
              const avg = (vals.reduce((s,v)=>s+v,0)/vals.filter(v=>v>0).length||0).toFixed(2);
              const today = new Date().getDay();
              return (
                <div key={wt.key} style={{ background:'var(--bg-primary)', borderRadius:'var(--radius-sm)', padding:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>{wt.label}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>Week avg: <span style={{ fontFamily:'DM Mono,monospace', fontWeight:500, color:wt.color }}>{avg} {wt.unit}</span></div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'flex-end', height:80 }}>
                    {WEEK_DAYS.map((day, i) => {
                      const h = vals[i] ? (vals[i]/max)*70 : 0;
                      const isToday = i === (today===0?6:today-1);
                      return (
                        <div key={day} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                          {vals[i]>0 && <div style={{ fontSize:9, color:wt.color, fontFamily:'DM Mono,monospace', fontWeight:500 }}>{vals[i].toFixed(1)}</div>}
                          <div style={{ width:'100%', height:h||2, background:isToday?wt.color:'var(--border)', borderRadius:'3px 3px 0 0', transition:'height 0.4s' }}/>
                          <div style={{ fontSize:10, color:isToday?wt.color:'var(--text-muted)', fontWeight:isToday?600:400 }}>{day}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid-2" style={{ gap:16 }}>
        {/* Production Status */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Production Flow</div>
              <div className="card-subtitle">BOX — PART — GANGSAW</div>
            </div>
          </div>
          {[
            { label:'Blocks Received',  val: prod.blocks_received  || 0, color:'var(--blue)' },
            { label:'Blocks Completed', val: prod.blocks_completed || 0, color:'var(--green)' },
            { label:'Parts Available',  val: prod.parts_available  || 0, color:'var(--amber)' },
            { label:'Slabs in Stock',   val: prod.slabs_in_stock   || 0, color:'var(--purple)' },
            { label:'Slabs Sold',       val: prod.slabs_sold       || 0, color:'var(--brand)' },
          ].map((item, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom: i < 4 ? '1px solid var(--border-light)' : 'none' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:item.color, flexShrink:0 }}/>
                <span style={{ fontSize:13, color:'var(--text-secondary)' }}>{item.label}</span>
              </div>
              <span style={{ fontFamily:'DM Mono, monospace', fontWeight:500, fontSize:18, color:item.color }}>{item.val}</span>
            </div>
          ))}
        </div>

        {/* Recent Sessions */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Work Sessions</div>
          </div>
          {sessions.slice(0,6).length===0 ? (
            <div style={{ padding:'20px 0', textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>No sessions yet</div>
          ) : (
            <div>
              {sessions.slice(0,6).map((s,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom: i<5?'1px solid var(--border-light)':'none' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--brand)' }}>{s.session_number}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
                      {s.direction&&`${s.direction} · `}{s.block_reference||'—'} · {s.started_date}
                    </div>
                  </div>
                  <span className={`badge ${s.status==='COMPLETED'?'badge-muted':s.status==='IN_PROGRESS'?'badge-green':'badge-amber'}`}>
                    {s.status?.replace('_',' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
